import { useEffect, useState, useCallback, useRef } from "react";
import { vendorMasterRepository } from "../repositories/vendorMaster.repository";


export const useOfflineVendorMaster = (
  authToken,
  org_token,
  searchQuery,
  pageNumber,
  pageSize = 10,
  sortKey = "",
  sortOrder = ""
) => {

  const [datas, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const lastRequestKeyRef = useRef("");
  const latestRequestIdRef = useRef(0);

  const loadData = useCallback(async (force = false) => {
    if (!org_token || !authToken) return;

    const trimmedSearch = searchQuery?.trim() || "";
    const requestKey = JSON.stringify({
      org_token,
      pageNumber,
      pageSize,
      searchQuery: trimmedSearch,
      sortKey: sortKey || "",
      sortOrder: sortOrder || "",
      online: navigator.onLine,
    });

    if (!force && lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;
    const requestId = ++latestRequestIdRef.current;

    setLoading(true);

    try {
      if (navigator.onLine) {
        const response = await vendorMasterRepository.smartSync(
          authToken,
          org_token,
          pageNumber,
          trimmedSearch,
          sortKey,
          sortOrder
        );

        if (requestId !== latestRequestIdRef.current) return;

        setData(response.data || []);
        setPagination({
          total_records: response.total_count || 0,
          page_count: response.total_pages || 0,
          current_page_count: response?.data?.length || 0,
        });
      } else {
        const cached = await vendorMasterRepository.getFromCache(
          org_token,
          trimmedSearch
        );

        let ordered = cached;
        if (sortKey === "created_date") {
          ordered = [...(cached || [])].sort((a, b) => {
            const aTime = new Date(a?.created_on || 0).getTime() || 0;
            const bTime = new Date(b?.created_on || 0).getTime() || 0;
            if (sortOrder === "descending") return bTime - aTime;
            return aTime - bTime;
          });
        }

        const start = (pageNumber - 1) * pageSize;
        const end = start + pageSize;
        const pageData = (ordered || []).slice(start, end);

        if (requestId !== latestRequestIdRef.current) return;

        setData(pageData);
        setPagination({
          total_records: (ordered || []).length,
          page_count: Math.ceil((ordered || []).length / pageSize),
          current_page_count: pageData.length,
        });
      }
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) return;
      setData([]);
      setPagination({
        total_records: 0,
        page_count: 0,
        current_page_count: 0,
      });
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [authToken, org_token, pageNumber, pageSize, searchQuery, sortKey, sortOrder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { datas, pagination, loading, refetch: () => loadData(true) };
};
